import { Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import {
  Firestore,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { firstValueFrom, Observable, switchMap } from 'rxjs';
import { Mesocycle, MesocycleInput, MesocycleState } from 'src/models/mesocycle.model';
import { doLocalDateRangesOverlap, isDateInInclusiveRange, toLocalDateKey } from 'src/utils/local-date';
import { MesocycleColorService } from './mesocycle-color.service';

/** Firestore access for the new mesocycles collection only. Never mutates history. */
@Injectable({ providedIn: 'root' })
export class MesocycleService {
  private readonly cachedByUser = new Map<string, Mesocycle[]>();

  constructor(
    private readonly firestore: Firestore,
    private readonly auth: Auth,
    private readonly colors: MesocycleColorService
  ) {}

  /** A live, owner-scoped list. Archived cycles remain visible for management. */
  listForCurrentUser(): Observable<Mesocycle[]> {
    return authState(this.auth).pipe(
      switchMap((user) => {
        if (!user) throw new Error('No authenticated user found.');
        return this.listForUser(user.uid);
      })
    );
  }

  /** Gets the active (non-archived) cycle for a local calendar day. */
  async getActiveForDate(userId: string, date = toLocalDateKey()): Promise<Mesocycle | undefined> {
    await this.assertCurrentUser(userId);
    const cycles = await this.getCachedOrFetch(userId);
    return cycles.find(
      (cycle) =>
        !cycle.archivedAt &&
        isDateInInclusiveRange(date, cycle.startDate, cycle.endDate)
    );
  }

  async getActiveForCurrentUser(date = toLocalDateKey()): Promise<Mesocycle | undefined> {
    const user = await this.requireUser();
    return this.getActiveForDate(user.uid, date);
  }

  async getById(id: string): Promise<Mesocycle | undefined> {
    const user = await this.requireUser();
    const snapshot = await getDoc(doc(this.firestore, 'mesocycles', id));
    if (!snapshot.exists()) return undefined;
    const cycle = { id: snapshot.id, ...(snapshot.data() as Mesocycle) };
    if (cycle.userId !== user.uid) throw new Error('Mesocycle does not belong to the current user.');
    return cycle;
  }

  async create(input: MesocycleInput): Promise<Mesocycle> {
    const user = await this.requireUser();
    const normalized = this.normalizeInput(input);
    await this.assertDoesNotOverlap(user.uid, normalized.startDate, normalized.endDate);

    const reference = doc(collection(this.firestore, 'mesocycles'));
    const cycle = {
      ...normalized,
      userId: user.uid,
      color: this.colors.colorFor(reference.id),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await setDoc(reference, cycle);
    this.cachedByUser.delete(user.uid);
    return { id: reference.id, ...cycle } as unknown as Mesocycle;
  }

  async update(id: string, input: MesocycleInput): Promise<void> {
    const existing = await this.requireOwned(id);
    if (existing.archivedAt) throw new Error('Archived mesocycles cannot be edited.');
    const normalized = this.normalizeInput(input);
    await this.assertDoesNotOverlap(existing.userId, normalized.startDate, normalized.endDate, id);
    await updateDoc(doc(this.firestore, 'mesocycles', id), {
      ...normalized,
      updatedAt: serverTimestamp(),
    });
    this.cachedByUser.delete(existing.userId);
  }

  /** Removes a cycle from active selection without changing any workout history. */
  async archive(id: string): Promise<void> {
    const existing = await this.requireOwned(id);
    if (existing.archivedAt) return;
    await updateDoc(doc(this.firestore, 'mesocycles', id), {
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    this.cachedByUser.delete(existing.userId);
  }

  /** Hard deletion is deliberately limited to cycles without linked plan history. */
  async deleteEmpty(id: string): Promise<void> {
    const existing = await this.requireOwned(id);
    const histories = await getDocs(
      query(
        collection(this.firestore, 'plansHistories'),
        where('userId', '==', existing.userId),
        where('mesocycleId', '==', id),
        limit(1)
      )
    );
    if (!histories.empty) {
      throw new Error('Mesocycles with workout history must be archived instead of deleted.');
    }
    await deleteDoc(doc(this.firestore, 'mesocycles', id));
    this.cachedByUser.delete(existing.userId);
  }

  stateFor(cycle: Mesocycle, today = toLocalDateKey()): MesocycleState {
    if (cycle.archivedAt) return 'archived';
    if (today < cycle.startDate) return 'upcoming';
    if (today > cycle.endDate) return 'past';
    return 'active';
  }

  private listForUser(userId: string): Observable<Mesocycle[]> {
    return new Observable<Mesocycle[]>((subscriber) => {
      const cyclesQuery = query(
        collection(this.firestore, 'mesocycles'),
        where('userId', '==', userId),
        orderBy('startDate', 'desc')
      );
      const unsubscribe = onSnapshot(
        cyclesQuery,
        (snapshot) => {
          const cycles = snapshot.docs.map((entry) => ({
            id: entry.id,
            ...(entry.data() as Mesocycle),
          }));
          this.cachedByUser.set(userId, cycles);
          subscriber.next(cycles);
        },
        (error) => subscriber.error(error)
      );
      return unsubscribe;
    });
  }

  private async getCachedOrFetch(userId: string): Promise<Mesocycle[]> {
    const cached = this.cachedByUser.get(userId);
    if (cached) return cached;
    const snapshot = await getDocs(
      query(
        collection(this.firestore, 'mesocycles'),
        where('userId', '==', userId),
        orderBy('startDate', 'desc')
      )
    );
    const cycles = snapshot.docs.map((entry) => ({
      id: entry.id,
      ...(entry.data() as Mesocycle),
    }));
    this.cachedByUser.set(userId, cycles);
    return cycles;
  }

  private async assertDoesNotOverlap(
    userId: string,
    startDate: string,
    endDate: string,
    excludingId?: string
  ): Promise<void> {
    const conflict = (await this.getCachedOrFetch(userId)).find(
      (cycle) =>
        cycle.id !== excludingId &&
        !cycle.archivedAt &&
        doLocalDateRangesOverlap(startDate, endDate, cycle.startDate, cycle.endDate)
    );
    if (conflict) throw new Error(`Mesocycle overlaps with "${conflict.name}".`);
  }

  private normalizeInput(input: MesocycleInput): MesocycleInput {
    const name = input.name?.trim();
    const description = input.description?.trim() || undefined;
    if (!name) throw new Error('Mesocycle name is required.');
    if (name.length > 80) throw new Error('Mesocycle name must be 80 characters or fewer.');
    if (description && description.length > 500) throw new Error('Mesocycle description must be 500 characters or fewer.');
    // Range helper validates format and ordering before the Firestore write.
    isDateInInclusiveRange(input.startDate, input.startDate, input.endDate);
    if (
      input.targetWorkoutsPerWeek !== undefined &&
      (!Number.isInteger(input.targetWorkoutsPerWeek) || input.targetWorkoutsPerWeek <= 0)
    ) {
      throw new Error('Target workouts per week must be a positive integer.');
    }
    return {
      name,
      ...(description ? { description } : {}),
      startDate: input.startDate,
      endDate: input.endDate,
      ...(input.targetWorkoutsPerWeek !== undefined
        ? { targetWorkoutsPerWeek: input.targetWorkoutsPerWeek }
        : {}),
    };
  }

  private async requireOwned(id: string): Promise<Mesocycle> {
    const cycle = await this.getById(id);
    if (!cycle) throw new Error('Mesocycle not found.');
    return cycle;
  }

  private async requireUser() {
    const user = await firstValueFrom(authState(this.auth));
    if (!user) throw new Error('No authenticated user found.');
    return user;
  }

  private async assertCurrentUser(userId: string): Promise<void> {
    const user = await this.requireUser();
    if (user.uid !== userId) throw new Error('Cannot access another user\'s mesocycles.');
  }
}
